import {
  Theme,
  ThemeOptions,
  createTheme as createMuiTheme,
} from '@mui/material/styles';

import { createTheme, ThemeExtension } from '@liscio/ui';

type StyleProps = { theme: Theme; ownerState: Record<string, unknown> };
type Style = unknown;
type ComponentConfig = {
  defaultProps?: Record<string, unknown>;
  styleOverrides?: Record<string, Style> | string;
  variants?: unknown[];
};
type ComponentsConfig = Record<string, ComponentConfig | undefined>;

// Legacy screens were laid out on a 5px spacing unit. Their call sites now
// express those distances on the shared 8px unit; these overrides keep the
// spacing MUI applies internally (via theme.spacing) at the legacy distances.
const LEGACY_SPACING_UNIT = 5;
const legacySpacing = (...factors: number[]) =>
  factors.map((factor) => `${factor * LEGACY_SPACING_UNIT}px`).join(' ');

const legacySpacingOverrides: ComponentsConfig = {
  MuiAccordionDetails: {
    styleOverrides: { root: { padding: legacySpacing(1, 2, 2) } },
  },
  MuiAccordionSummary: {
    styleOverrides: { root: { padding: legacySpacing(0, 2) } },
  },
  MuiContainer: {
    styleOverrides: {
      root: ({ theme, ownerState }: StyleProps) =>
        ownerState.disableGutters
          ? {}
          : {
              paddingLeft: legacySpacing(2),
              paddingRight: legacySpacing(2),
              [theme.breakpoints.up('sm')]: {
                paddingLeft: legacySpacing(3),
                paddingRight: legacySpacing(3),
              },
            },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: ({ ownerState }: StyleProps) => {
        if (ownerState.variant !== 'middle') return {};
        return ownerState.orientation === 'vertical'
          ? { marginTop: legacySpacing(1), marginBottom: legacySpacing(1) }
          : { marginLeft: legacySpacing(2), marginRight: legacySpacing(2) };
      },
      wrapper: {
        paddingLeft: legacySpacing(1.2),
        paddingRight: legacySpacing(1.2),
      },
      wrapperVertical: {
        paddingTop: legacySpacing(1.2),
        paddingBottom: legacySpacing(1.2),
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        '& + .MuiDivider-root': {
          marginTop: legacySpacing(1),
          marginBottom: legacySpacing(1),
        },
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ ownerState }: StyleProps) => {
        if (!ownerState.icon || !ownerState.label) return {};
        if (ownerState.iconPosition === 'start') {
          return { '& > .MuiTab-iconWrapper': { marginRight: legacySpacing(1) } };
        }
        if (ownerState.iconPosition === 'end') {
          return { '& > .MuiTab-iconWrapper': { marginLeft: legacySpacing(1) } };
        }
        return {};
      },
    },
  },
  MuiTable: {
    styleOverrides: { root: { '& caption': { padding: legacySpacing(2) } } },
  },
  MuiToolbar: {
    styleOverrides: {
      root: ({ theme, ownerState }: StyleProps) =>
        ownerState.disableGutters
          ? {}
          : {
              paddingLeft: legacySpacing(2),
              paddingRight: legacySpacing(2),
              [theme.breakpoints.up('sm')]: {
                paddingLeft: legacySpacing(3),
                paddingRight: legacySpacing(3),
              },
            },
    },
  },
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  Object.getPrototypeOf(value) === Object.prototype;

const resolveStyle = (style: Style, props: StyleProps) =>
  typeof style === 'function' ? style(props) : style;

const clearKeys = (value: Record<string, unknown>) =>
  Object.fromEntries(Object.keys(value).map((key) => [key, undefined]));

/** Layers `later` component styles over `earlier` ones, slot by slot. */
const mergeComponents = (
  earlier: ComponentsConfig,
  later: ComponentsConfig = {},
): ComponentsConfig => {
  const merged: ComponentsConfig = { ...later };
  Object.entries(earlier).forEach(([name, config]) => {
    const laterConfig = later[name];
    const laterStyles = laterConfig?.styleOverrides;
    if (laterStyles === undefined) {
      merged[name] = { ...laterConfig, styleOverrides: config?.styleOverrides };
      return;
    }
    if (!isPlainObject(laterStyles)) {
      merged[name] = laterConfig;
      return;
    }
    const styleOverrides: Record<string, Style> = { ...laterStyles };
    Object.entries(config?.styleOverrides ?? {}).forEach(([slot, style]) => {
      const laterStyle = laterStyles[slot];
      styleOverrides[slot] =
        laterStyle === undefined
          ? style
          : (props: StyleProps) => ({
              ...(resolveStyle(style, props) as object),
              ...(resolveStyle(laterStyle, props) as object),
            });
    });
    merged[name] = { ...laterConfig, styleOverrides };
  });
  return merged;
};

/**
 * Replaces the shared theme's component configuration with the legacy one.
 * Every shared default prop and style slot is reset, so a component only
 * gets the styles the legacy theme gives it.
 */
const replaceComponents = (
  shared: ComponentsConfig = {},
  legacy: ComponentsConfig = {},
): ComponentsConfig => {
  const names = new Set([...Object.keys(shared), ...Object.keys(legacy)]);
  const replaced: ComponentsConfig = {};
  names.forEach((name) => {
    const sharedConfig = shared[name] ?? {};
    const legacyConfig = legacy[name] ?? {};
    const legacyStyles = legacyConfig.styleOverrides;
    if (legacyStyles !== undefined && !isPlainObject(legacyStyles)) {
      // e.g. MuiCssBaseline's global stylesheet string
      replaced[name] = legacyConfig;
      return;
    }
    const sharedStyles = isPlainObject(sharedConfig.styleOverrides)
      ? sharedConfig.styleOverrides
      : {};
    const styleOverrides: Record<string, Style> = {};
    new Set([
      ...Object.keys(sharedStyles),
      ...Object.keys(legacyStyles ?? {}),
    ]).forEach((slot) => {
      const style = legacyStyles?.[slot];
      // Functions replace rather than deep-merge with the shared slot.
      styleOverrides[slot] =
        typeof style === 'function' ? style : () => style ?? {};
    });
    replaced[name] = {
      ...legacyConfig,
      defaultProps: {
        ...clearKeys(sharedConfig.defaultProps ?? {}),
        ...legacyConfig.defaultProps,
      },
      styleOverrides,
      variants: legacyConfig.variants ?? [],
    };
  });
  return replaced;
};

/** Replaces each shared typography variant with the legacy variant. */
const replaceTypography = (
  shared: Record<string, unknown>,
  legacy: Record<string, unknown>,
) => {
  const replaced: Record<string, unknown> = { ...legacy };
  Object.entries(shared).forEach(([key, sharedValue]) => {
    const legacyValue = legacy[key];
    if (isPlainObject(sharedValue) && isPlainObject(legacyValue)) {
      replaced[key] = { ...clearKeys(sharedValue), ...legacyValue };
    }
  });
  return replaced;
};

/**
 * Creates a legacy theme on top of the shared @liscio/ui theme.
 *
 * The shared base supplies the 8px spacing unit and shared design tokens.
 * The legacy palette, typography, shape, and component styles replace the
 * shared ones, so legacy screens keep their appearance while code written
 * against them uses the same spacing unit as the firm app.
 */
export const createLegacyTheme = ({
  components,
  ...appearance
}: ThemeOptions) => {
  const legacyTheme = createMuiTheme(appearance);
  const legacyExtension: ThemeExtension = (sharedTheme) =>
    ({
      palette: legacyTheme.palette,
      shape: legacyTheme.shape,
      typography: replaceTypography(
        sharedTheme.typography as unknown as Record<string, unknown>,
        legacyTheme.typography as unknown as Record<string, unknown>,
      ),
      components: replaceComponents(
        sharedTheme.components as ComponentsConfig,
        mergeComponents(
          legacySpacingOverrides,
          components as ComponentsConfig,
        ),
      ),
    }) as ThemeOptions;

  return createTheme({ extensions: [legacyExtension] });
};
